import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FREE_TIER_LIMITS } from '../types'
import { createCheckoutSession } from '../services/subscriptionRepo'
import { PRICING } from '../utils/subscription'
import { trackInitiateCheckout } from '../utils/facebookPixel'
import { trackGABeginCheckout } from '../utils/googleAnalytics'
import { trackLinkedInKeyPageView } from '../utils/linkedinTracking'
import { generateEventId } from '../utils/trackingCookies'

type Props = {
  userId: string
  isPro: boolean
}

type BillingPeriod = 'monthly' | 'quarterly' | 'lifetime'

const isValidBillingPeriod = (value: string | null): value is BillingPeriod => {
  return value === 'monthly' || value === 'quarterly' || value === 'lifetime'
}

export const PricingPage = ({ userId, isPro }: Props) => {
  const [searchParams] = useSearchParams()
  const initialPlan = searchParams.get('plan')
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(
    isValidBillingPeriod(initialPlan) ? initialPlan : 'quarterly'
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track LinkedIn key page view conversion on mount
  useEffect(() => {
    trackLinkedInKeyPageView()
  }, [])

  const handleUpgrade = async (priceType: BillingPeriod) => {
    setIsLoading(true)
    setError(null)

    try {
      const eventId = generateEventId()

      // Track conversion events (before redirect, with eventId for CAPI dedup)
      trackInitiateCheckout(priceType, PRICING[priceType].amount, eventId)
      trackGABeginCheckout(priceType, PRICING[priceType].amount)

      const url = await createCheckoutSession(
        userId,
        priceType,
        `${window.location.origin}/billing?success=true&plan=${priceType}&amount=${PRICING[priceType].amount}&event_id=${encodeURIComponent(eventId)}`,
        `${window.location.origin}/billing?cancelled=true`,
        eventId
      )
      window.location.href = url
    } catch (err) {
      console.error('Upgrade error:', err)
      setError(err instanceof Error ? err.message : 'Failed to start checkout')
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-app-page">
      <div className="mx-auto max-w-4xl px-4 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold text-app-ink font-display">Choose your plan</h1>
            <p className="text-app-muted">Start free, upgrade when you need more power</p>
          </div>

          {/* Billing period toggle */}
          <div className="flex justify-center">
            <div className="inline-flex rounded-lg border border-app-line bg-app-subtle p-1">
              {(['monthly', 'quarterly', 'lifetime'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setBillingPeriod(period)}
                  className={`relative rounded-md px-5 py-2.5 text-sm font-medium transition-all ${
                    billingPeriod === period
                      ? 'bg-app-highlight text-app-accent shadow-app-card'
                      : 'text-app-muted hover:text-app-ink'
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                  {period === 'quarterly' && billingPeriod === period && (
                    <span className="absolute -top-2 -right-2 rounded-full bg-app-highlight text-app-accent px-1.5 py-0.5 text-[10px] font-bold">
                      Save 19%
                    </span>
                  )}
                  {period === 'lifetime' && billingPeriod === period && (
                    <span className="absolute -top-2 -right-2 rounded-full bg-amber-500 dark:bg-app-highlight dark:text-app-accent px-1.5 py-0.5 text-[10px] font-bold text-white">
                      Best value
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Plans comparison */}
          <div className="grid gap-6 md:grid-cols-[5fr_6fr] items-start">
            {/* Free plan */}
            <div className={`card p-5 space-y-4 text-sm mt-8 ${!isPro ? 'ring-2 ring-app-line' : ''}`}>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-xl font-semibold text-app-ink">Free</p>
                  {!isPro && (
                    <span className="rounded-full bg-app-line px-2 py-0.5 text-xs text-app-ink">Current plan</span>
                  )}
                </div>
                <p className="text-4xl font-bold text-app-ink mt-2">$0<span className="text-lg font-normal text-app-muted">/forever</span></p>
                <p className="text-sm text-app-muted mt-1">Perfect for getting started</p>
              </div>
              <ul className="space-y-3 text-sm text-app-ink">
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Up to {FREE_TIER_LIMITS.maxActiveApps} active applications
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Pipeline tracking & stages
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Favorites & quick filters
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  AI job data extraction
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  1 free resume analysis
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  1 free interview quiz
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  1 free mock interview
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-app-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  <span className="text-app-muted">Advanced analytics & Sankey</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-app-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  <span className="text-app-muted">Multi-currency conversion</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-app-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  <span className="text-app-muted">Salary insights & comparison</span>
                </li>
              </ul>
              {!isPro && (
                <div className="pt-2">
                  <p className="text-center text-sm text-app-muted">You're on this plan</p>
                </div>
              )}
            </div>

            {/* Pro plan */}
            <div className={`card p-8 space-y-6 relative ${isPro ? 'ring-2 ring-emerald-500 dark:ring-emerald-600' : 'ring-2 ring-app-accent'}`}>
              <span className={`absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-xs font-semibold ${isPro ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' : 'bg-app-primary text-white'}`}>
                {isPro ? 'Current plan' : 'Recommended'}
              </span>
              <div>
                <p className="text-xl font-semibold text-app-ink">Pro</p>
                <p className="text-4xl font-bold text-app-ink mt-2">
                  ${PRICING[billingPeriod].amount}
                  <span className="text-lg font-normal text-app-muted">
                    {billingPeriod === 'monthly' ? '/mo' : billingPeriod === 'quarterly' ? '/qtr' : ' once'}
                  </span>
                </p>
                {billingPeriod === 'monthly' && (
                  <p className="text-sm text-app-muted mt-1">Billed monthly, cancel anytime</p>
                )}
                {billingPeriod === 'quarterly' && (
                  <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">That's just $5.67/month</p>
                )}
                {billingPeriod === 'lifetime' && (
                  <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">One-time payment, yours forever</p>
                )}
              </div>
              <ul className="space-y-3 text-sm text-app-ink">
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <strong>Unlimited</strong> applications
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Pipeline tracking & stages
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Favorites & quick filters
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Advanced analytics & Sankey
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Multi-currency conversion (11 currencies)
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Salary insights & comparison
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Unlimited resume analyses
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Resume Match - compare CV to jobs
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Resume Builder with AI tailoring & PDF export
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Unlimited interview quizzes
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  AI mock interviews (2/week)
                </li>
                <li className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  AI Assistant
                </li>
              </ul>
              {!isPro ? (
                <button
                  id="subscribe-pro-btn"
                  onClick={() => handleUpgrade(billingPeriod)}
                  disabled={isLoading}
                  aria-label={`Subscribe to Pro plan - ${PRICING[billingPeriod].amount} USD ${billingPeriod}`}
                  data-price={PRICING[billingPeriod].amount}
                  data-value={PRICING[billingPeriod].amount}
                  data-currency="USD"
                  data-billing-period={billingPeriod}
                  data-content-name={`Pro Subscription - ${billingPeriod.charAt(0).toUpperCase() + billingPeriod.slice(1)}`}
                  data-content-ids={`["pro-${billingPeriod}"]`}
                  data-content-type="product"
                  data-event-category="subscription"
                  data-event-action="initiate_checkout"
                  data-event-label={`Pro - ${billingPeriod}`}
                  data-plan-type="pro"
                  className="relative w-full rounded-lg bg-app-primary px-4 py-3 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-60 transition-colors shadow-app-card"
                >
                  {isLoading ? 'Loading...' : 'Subscribe to Pro'}
                  {/* Price hidden visually but accessible to Facebook Pixel */}
                  <span className="absolute opacity-0 pointer-events-none h-0 overflow-hidden" data-fb-price="true" data-fb-currency="USD">
                    ${PRICING[billingPeriod].amount}
                  </span>
                </button>
              ) : (
                <div className="pt-2">
                  <p className="text-center text-sm text-emerald-700 dark:text-emerald-400">You're on this plan</p>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-rose-300 dark:border-rose-900/50 bg-rose-100 dark:bg-rose-950/30 px-4 py-3">
              <p className="text-sm text-rose-900 dark:text-rose-200">{error}</p>
            </div>
          )}

          {/* FAQ */}
          <div className="card p-6">
            <p className="font-semibold text-app-ink mb-4">Frequently Asked Questions</p>
            <div className="grid gap-6 md:grid-cols-2 text-sm">
              <div>
                <p className="text-app-ink font-medium">Can I cancel anytime?</p>
                <p className="text-app-muted mt-1">
                  Yes! You can cancel your subscription at any time. You'll keep access until the end of your billing period.
                </p>
              </div>
              <div>
                <p className="text-app-ink font-medium">What happens to my data if I downgrade?</p>
                <p className="text-app-muted mt-1">
                  Your data is safe. On free plan, you can view all applications but can only have {FREE_TIER_LIMITS.maxActiveApps} active at a time.
                </p>
              </div>
              <div>
                <p className="text-app-ink font-medium">Do you offer refunds?</p>
                <p className="text-app-muted mt-1">
                  Yes, we offer a 14-day money-back guarantee. Contact us if you're not satisfied.
                </p>
              </div>
              <div>
                <p className="text-app-ink font-medium">What payment methods do you accept?</p>
                <p className="text-app-muted mt-1">
                  We accept all major credit cards, Google Pay, and Apple Pay via Stripe.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
